import { TOC, getChapterVerseCount } from '@skjv/common';
import { CloseIcon, ChevronLeftIcon } from '@components/icons';

interface TableOfContentsProps {
  isOpen: boolean;
  selectedTestament: 0 | 1;
  selectedBookIndex: number | null;
  selectedChapterIndex: number | null;
  onTestamentChange: (testament: 0 | 1) => void;
  onBookSelect: (bookIndex: number | null) => void;
  onChapterSelect: (chapterIndex: number | null) => void;
  onNavigate: (
    testamentIndex: number,
    bookIndex: number,
    chapterIndex: number,
    verseIndex: number,
  ) => void;
  onClose: () => void;
}

/**
 * Table of Contents overlay component with Apple Liquid Glass styling.
 * Navigation: Testament -> Book -> Chapter -> Verse (a verse tap navigates).
 */
export function TableOfContents({
  isOpen,
  selectedTestament,
  selectedBookIndex,
  selectedChapterIndex,
  onTestamentChange,
  onBookSelect,
  onChapterSelect,
  onNavigate,
  onClose,
}: TableOfContentsProps) {
  const books = selectedTestament === 0 ? TOC.OT : TOC.NT;
  const selectedBook = selectedBookIndex !== null ? books[selectedBookIndex] : null;
  const verseCount =
    selectedBook && selectedChapterIndex !== null
      ? getChapterVerseCount(selectedTestament, selectedBookIndex!, selectedChapterIndex)
      : 0;

  // Determine which view to show
  const showVerses = selectedBook && selectedChapterIndex !== null;
  const showChapters = selectedBook && selectedChapterIndex === null;

  return (
    <div
      className={`toc-overlay ${isOpen ? 'open' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="toc-panel">
        {/* Header */}
        <div className="toc-header">
          {showVerses ? (
            <h3 className="toc-header-title">
              {selectedBook.name} {selectedChapterIndex! + 1}
            </h3>
          ) : showChapters ? (
            <h3 className="toc-header-title">{selectedBook.name}</h3>
          ) : (
            <div className="toc-testament-tabs">
              <button
                onClick={() => onTestamentChange(0)}
                className={`toc-tab ${selectedTestament === 0 ? 'active' : ''}`}
              >
                Old Testament
              </button>
              <button
                onClick={() => onTestamentChange(1)}
                className={`toc-tab ${selectedTestament === 1 ? 'active' : ''}`}
              >
                New Testament
              </button>
            </div>
          )}
          <button onClick={onClose} className="toc-close-btn">
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="toc-content">
          {showVerses ? (
            // Verse grid — a verse tap navigates and dismisses
            <div className="toc-chapters">
              <div className="toc-chapter-grid">
                <button
                  onClick={() => onChapterSelect(null)}
                  className="toc-chapter-btn toc-back-tile"
                >
                  <ChevronLeftIcon className="h-8 w-8" />
                </button>
                {Array.from({ length: verseCount }, (_, i) => (
                  <button
                    key={i}
                    onClick={() =>
                      onNavigate(selectedTestament, selectedBookIndex!, selectedChapterIndex!, i)
                    }
                    className="toc-chapter-btn toc-verse-btn"
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          ) : showChapters ? (
            // Chapter grid — a chapter tap opens its verse grid
            <div className="toc-chapters">
              <div className="toc-chapter-grid">
                <button
                  onClick={() => onBookSelect(null)}
                  className="toc-chapter-btn toc-back-tile"
                >
                  <ChevronLeftIcon className="h-8 w-8" />
                </button>
                {Array.from({ length: selectedBook.chapterCount }, (_, i) => (
                  <button key={i} onClick={() => onChapterSelect(i)} className="toc-chapter-btn">
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            // Book list
            <div className="toc-books">
              {books.map((book, index) => (
                <button
                  key={book.name}
                  onClick={() => onBookSelect(index)}
                  className="toc-book-btn"
                >
                  <span className="toc-book-name">{book.abbrev}</span>
                  <span className="toc-book-chapters">{book.chapterCount} chapters</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
